"use client";

import * as PopoverPrimitives from "@radix-ui/react-popover";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  RiAddLine,
  RiArrowDownSLine,
  RiCheckLine,
  RiSearchLine,
} from "react-icons/ri";

import AIBadge from "@/components/ui/ai-badge";
import { AIToolbar } from "@/components/ui/ai-toolbar";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { useAIGenerationWithStream } from "@/hooks/use-ai-generation-with-stream";

import { cx, focusInput, hasErrorInput } from "@/lib/tailwind-utils";

// Context for sharing state between components
type SearchSelectContextType = {
  open: boolean;
  disabled?: boolean;
  setOpen: (open: boolean) => void;
  value: string;
  onValueChange: (value: string) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  // Populated by SearchSelectTrigger; used by SearchSelectContent to
  // find the nearest open Dialog ancestor for auto-portaling.
  triggerRef: React.MutableRefObject<HTMLElement | null>;
};

const SearchSelectContext = createContext<SearchSelectContextType | undefined>(
  undefined,
);

const useSearchSelect = () => {
  const context = useContext(SearchSelectContext);
  if (context === undefined) {
    throw new Error(
      "SearchSelect components must be used within a SearchSelect",
    );
  }
  return context;
};

// Root component
interface SearchSelectProps {
  value?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
  disabled?: boolean;
  /** Called when the popover open state changes */
  onOpenChange?: (open: boolean) => void;
  /** If provided, enables AI suggestion features. Function returns an async generator. */
  onGenerateAI?: () => AsyncGenerator<string>;
  /** Called when AI suggests a new option that should be created */
  onCreateOption?: (value: string) => void;
  /** Available options for matching AI suggestions */
  options?: Array<{ value: string; label: string }>;
  /** Function to get a badge for AI suggestions */
  getSuggestionBadge?: (
    suggestion: string,
  ) => { label: string; variant: "success" | "info" | "error" } | null;
  /** Called when the internal search term changes */
  onSearchTermChange?: (term: string) => void;
}

const SearchSelect = ({
  value = "",
  onValueChange,
  children,
  disabled,
  onOpenChange,
  onGenerateAI,
  onCreateOption,
  options = [],
  getSuggestionBadge,
  onSearchTermChange,
}: SearchSelectProps) => {
  const [open, setOpenInternal] = useState(false);
  const [searchTermInternal, setSearchTermInternal] = useState("");
  const triggerRef = useRef<HTMLElement | null>(null);

  const setSearchTerm = useCallback(
    (term: string) => {
      setSearchTermInternal(term);
      onSearchTermChange?.(term);
    },
    [onSearchTermChange],
  );

  const searchTerm = searchTermInternal;

  const setOpen = useCallback(
    (newOpen: boolean) => {
      if (newOpen && disabled) return;
      setOpenInternal(newOpen);
      onOpenChange?.(newOpen);
    },
    [onOpenChange, disabled],
  );

  // AI state
  const [suggestionMode, setSuggestionMode] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [feedbackHistory, setFeedbackHistory] = useState<string[]>([]);
  const [pendingSuggestion, setPendingSuggestion] = useState<string | null>(
    null,
  );

  const aiGeneration = useAIGenerationWithStream({
    onTextUpdate: (text) => {
      const suggestion = text.trim();
      setPendingSuggestion(suggestion);

      // Find matching option and pre-select it
      const matchingOption = options.find(
        (opt) =>
          opt.value.toLowerCase() === suggestion.toLowerCase() ||
          opt.label.toLowerCase() === suggestion.toLowerCase(),
      );

      if (matchingOption) {
        onValueChange?.(matchingOption.value);
      }
    },
  });

  const aiIsGenerating = aiGeneration.isGenerating;
  const aiError = aiGeneration.error;
  const aiCancel = aiGeneration.cancel;
  const aiReset = () => {
    setPendingSuggestion(null);
    aiGeneration.reset();
  };

  const handleValueChange = (newValue: string) => {
    if (disabled) return;
    onValueChange?.(newValue);
    setOpen(false);
    setSearchTerm("");
    // Clear suggestion mode if user manually selects
    if (suggestionMode) {
      setSuggestionMode(false);
      setPendingSuggestion(null);
      setFeedback("");
      setFeedbackHistory([]);
    }
  };

  const handleGenerate = useCallback(async () => {
    if (!onGenerateAI) return;
    setSuggestionMode(true);
    setFeedback("");
    setFeedbackHistory([]);
    setPendingSuggestion(null);
    await aiGeneration.generate(onGenerateAI);
  }, [onGenerateAI, aiGeneration]);

  const handleRetry = useCallback(async () => {
    if (!onGenerateAI) return;
    await aiGeneration.generate(onGenerateAI);
  }, [onGenerateAI, aiGeneration]);

  const handleRegenerate = useCallback(async () => {
    if (feedback.trim()) {
      setFeedbackHistory((prev) => [...prev, feedback.trim()]);
    }
    if (!onGenerateAI) return;
    // For SearchSelect, regenerate just calls generate again
    await aiGeneration.generate(onGenerateAI);
    setFeedback("");
  }, [feedback, onGenerateAI, aiGeneration]);

  const handleAccept = useCallback(() => {
    if (pendingSuggestion) {
      const matchingOption = options.find(
        (opt) =>
          opt.value.toLowerCase() === pendingSuggestion.toLowerCase() ||
          opt.label.toLowerCase() === pendingSuggestion.toLowerCase(),
      );

      if (matchingOption) {
        onValueChange?.(matchingOption.value);
      } else if (onCreateOption) {
        onCreateOption(pendingSuggestion);
        onValueChange?.(pendingSuggestion);
      }
    }
    setSuggestionMode(false);
    setFeedback("");
    setFeedbackHistory([]);
    setPendingSuggestion(null);
  }, [pendingSuggestion, options, onCreateOption, onValueChange]);

  const handleReject = useCallback(() => {
    aiCancel();
    setSuggestionMode(false);
    setFeedback("");
    setFeedbackHistory([]);
    setPendingSuggestion(null);
    aiReset();
  }, [aiCancel, aiReset]);

  const matchingOption = pendingSuggestion
    ? options.find(
        (opt) =>
          opt.value.toLowerCase() === pendingSuggestion.toLowerCase() ||
          opt.label.toLowerCase() === pendingSuggestion.toLowerCase(),
      )
    : null;
  const isNewSuggestion = pendingSuggestion && !matchingOption;
  const badge = getSuggestionBadge
    ? getSuggestionBadge(pendingSuggestion || "")
    : null;

  // If onGenerateAI provided, wrap with AI features
  if (onGenerateAI) {
    return (
      <div
        className={cx(
          "overflow-hidden rounded-xl border border-border bg-background transition-all",
        )}
      >
        <SearchSelectContext.Provider
          value={{
            open,
            disabled,
            setOpen,
            value,
            onValueChange: handleValueChange,
            searchTerm,
            setSearchTerm,
            triggerRef,
          }}
        >
          <PopoverPrimitives.Root open={open} onOpenChange={setOpen}>
            {children}
          </PopoverPrimitives.Root>
        </SearchSelectContext.Provider>

        {/* AI Toolbar */}
        {suggestionMode ? (
          <AIToolbar
            isGenerating={aiIsGenerating}
            hasError={!!aiError}
            hasGeneratedText={!!pendingSuggestion}
            feedbackHistoryCount={feedbackHistory.length}
            feedback={feedback}
            onFeedbackChange={setFeedback}
            onAccept={handleAccept}
            onRetry={handleRetry}
            onRegenerate={handleRegenerate}
            onCancel={handleReject}
            badge={badge}
            infoText={
              !aiIsGenerating && !aiError && pendingSuggestion
                ? isNewSuggestion
                  ? `will create "${pendingSuggestion}"`
                  : `selected "${pendingSuggestion}"`
                : undefined
            }
          />
        ) : (
          !disabled && (
            <button
              type="button"
              onClick={handleGenerate}
              className="flex w-full items-center gap-1.5 border-t border-border bg-background-secondary px-2 py-1.5 transition-colors hover:bg-hover"
            >
              <AIBadge size={14} />
              <span className="text-caption-strong text-primary">
                Suggest...
              </span>
            </button>
          )
        )}
      </div>
    );
  }

  return (
    <SearchSelectContext.Provider
      value={{
        open,
        disabled,
        setOpen,
        value,
        onValueChange: handleValueChange,
        searchTerm,
        setSearchTerm,
        triggerRef,
      }}
    >
      <PopoverPrimitives.Root open={open} onOpenChange={setOpen}>
        {children}
      </PopoverPrimitives.Root>
    </SearchSelectContext.Provider>
  );
};

SearchSelect.displayName = "SearchSelect";

// Trigger component
interface SearchSelectTriggerProps extends React.ComponentPropsWithoutRef<
  typeof PopoverPrimitives.Trigger
> {
  hasError?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  /** Allow the selected value to wrap onto multiple lines instead of truncating */
  wrap?: boolean;
}

const SearchSelectTrigger = React.forwardRef<
  React.ComponentRef<typeof PopoverPrimitives.Trigger>,
  SearchSelectTriggerProps
>(
  (
    { className, hasError, disabled, errorMessage, wrap, children, ...props },
    forwardedRef,
  ) => {
    const { open, triggerRef, disabled: rootDisabled } = useSearchSelect();

    const composedRef = (node: HTMLButtonElement | null) => {
      triggerRef.current = node;
      if (typeof forwardedRef === "function") {
        forwardedRef(node);
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }
    };

    return (
      <>
        <PopoverPrimitives.Trigger
          ref={composedRef}
          className={cx(
            "group/trigger flex w-full select-none items-center justify-between gap-1 rounded-xl border px-3 py-2 shadow-sm outline-none transition",
            wrap ? "text-left" : "truncate",
            "text-body",
            "text-foreground",
            "hover:bg-row-hover",
            "disabled:bg-muted disabled:text-foreground-muted",
            focusInput,
            hasError && hasErrorInput,
            className,
          )}
          disabled={disabled || rootDisabled}
          {...props}
        >
          <span className={cx(wrap ? "min-w-0 break-words" : "truncate")}>
            {children}
          </span>
          <RiArrowDownSLine
            className={cx(
              "size-4 shrink-0",
              "text-foreground-muted",
              "group-data-[disabled]/trigger:text-foreground-muted",
              "transition-transform",
              open && "rotate-180",
            )}
          />
        </PopoverPrimitives.Trigger>
        {hasError && errorMessage && (
          <p className="mt-1.5 text-caption text-status-error-text">
            {errorMessage}
          </p>
        )}
      </>
    );
  },
);

SearchSelectTrigger.displayName = "SearchSelectTrigger";

// Value component
interface SearchSelectValueProps {
  placeholder?: string;
  formatValue?: (value: string) => string;
  className?: string;
}

const SearchSelectValue = ({
  placeholder,
  formatValue,
  className = "",
}: SearchSelectValueProps) => {
  const { value } = useSearchSelect();

  if (!value && placeholder) {
    return <span className="text-foreground-muted">{placeholder}</span>;
  }

  const displayValue = formatValue ? formatValue(value) : value;

  return <span className={cx(className)}>{displayValue}</span>;
};

SearchSelectValue.displayName = "SearchSelectValue";

/**
 * Drive overflow scrolling from touch and trackpad deltas on touch devices.
 *
 * iPadOS / iOS Safari (notably 26.x) often fails to native-scroll nested
 * `overflow` regions inside Radix Popper's `position: fixed` + transformed
 * popover content. Dialogs make it worse: `react-remove-scroll` attaches
 * non-passive document listeners, and Magic Keyboard wheel gestures can
 * scroll the document instead of this list. Writing `scrollTop` still works,
 * so we take over both input paths and prevent Safari's incorrect default
 * scroll behavior.
 *
 * Uses a callback ref (not useEffect + object ref) because the scroller only
 * mounts when the popover opens via Radix Presence — an effect on the parent
 * Content wrapper would run once while closed, see a null ref, and never
 * attach listeners.
 */
function useManualOverflowScrollFallback() {
  const cleanupRef = useRef<(() => void) | null>(null);
  const scrollerRef = useRef<HTMLElement | null>(null);

  const setScrollerNode = useCallback((el: HTMLElement | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    scrollerRef.current = el;

    if (!el) return;
    if (typeof window === "undefined" || navigator.maxTouchPoints === 0) {
      return;
    }

    let startY = 0;
    let startScrollTop = 0;
    let tracking = false;

    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) return;
      tracking = true;
      startY = event.touches[0].clientY;
      startScrollTop = el.scrollTop;
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!tracking || event.touches.length !== 1) return;

      const maxScroll = el.scrollHeight - el.clientHeight;
      if (maxScroll <= 0) return;

      const delta = startY - event.touches[0].clientY;
      el.scrollTop = Math.max(0, Math.min(maxScroll, startScrollTop + delta));

      if (event.cancelable) {
        event.preventDefault();
      }
    };

    const onTouchEnd = () => {
      tracking = false;
    };

    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) return;

      let deltaY = event.deltaY;
      if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
        deltaY *= 16;
      } else if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
        deltaY *= el.clientHeight;
      }
      if (deltaY === 0) return;

      if (event.cancelable) {
        event.preventDefault();
      }
      event.stopPropagation();

      const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
      el.scrollTop = Math.max(0, Math.min(maxScroll, el.scrollTop + deltaY));
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });
    el.addEventListener("wheel", onWheel, { passive: false, capture: true });

    cleanupRef.current = () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
      el.removeEventListener("wheel", onWheel, { capture: true });
    };
  }, []);

  useEffect(() => () => cleanupRef.current?.(), []);

  return { scrollerRef, setScrollerNode };
}

// Content component
interface SearchSelectContentProps {
  searchPlaceholder?: string;
  emptyMessage?: string;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  /** Optional action element to render at the bottom of the dropdown (e.g., "Add new" button) */
  footerAction?: React.ReactNode;
  /** Called when the search input value changes */
  onSearchChange?: (value: string) => void;
  /** Container to portal the dropdown into (e.g. when inside a Dialog to fix positioning) */
  container?: HTMLElement | null;
}

const SearchSelectContent = React.forwardRef<
  React.ComponentRef<typeof PopoverPrimitives.Content>,
  SearchSelectContentProps
>(
  (
    {
      searchPlaceholder = "Search...",
      emptyMessage = "No options found.",
      children,
      className,
      disabled,
      footerAction,
      onSearchChange,
      container,
      ...props
    },
    forwardedRef,
  ) => {
    const {
      searchTerm,
      setSearchTerm,
      setOpen,
      value,
      triggerRef,
      disabled: rootDisabled,
    } = useSearchSelect();
    const isDisabled = disabled || rootDisabled;
    const { scrollerRef, setScrollerNode } = useManualOverflowScrollFallback();

    // Auto-portal into the nearest open Radix Dialog overlay/root when the
    // caller didn't supply an explicit container. `document.body` is outside
    // Radix Dialog's `react-remove-scroll` subtree, so iOS / iPadOS Safari
    // defaultPrevents touch-scrolling popover lists there. The dialog content
    // itself is also not a safe container: drawers/dialogs often use
    // `backdrop-filter` or transforms, and desktop Safari treats those as
    // containing blocks for Radix Popper's fixed-position wrapper, which can
    // push the dropdown off-screen. Portaling to the content's parent keeps the
    // popover inside the scroll-lock allowlist while avoiding those fixed
    // containing blocks. `closest()` from the trigger picks the correct dialog
    // even with nested dialogs. An explicit `container={null}` from the caller
    // still wins (Radix interprets null as "use body").
    const [autoContainer, setAutoContainer] = useState<HTMLElement | null>(
      null,
    );
    useLayoutEffect(() => {
      if (container !== undefined) return;
      const dialog = triggerRef.current?.closest<HTMLElement>(
        '[role="dialog"][data-state="open"]',
      );
      setAutoContainer(dialog?.parentElement ?? dialog ?? null);
    }, [container, triggerRef]);
    const portalContainer = container !== undefined ? container : autoContainer;

    const handleSearchChange = (value: string) => {
      setSearchTerm(value);
      onSearchChange?.(value);
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (["Escape"].includes(e.key)) {
        setOpen(false);
      }
      if (["ArrowDown", "ArrowUp", "Enter"].includes(e.key)) {
        e.preventDefault();
      }
    };

    // Recursively extract all text content from nested elements
    const extractTextContent = (element: React.ReactNode): string => {
      if (typeof element === "string" || typeof element === "number") {
        return String(element);
      }
      if (React.isValidElement(element) && element.props) {
        return React.Children.toArray((element.props as any).children)
          .map(extractTextContent)
          .join(" ");
      }
      if (Array.isArray(element)) {
        return element.map(extractTextContent).join(" ");
      }
      return "";
    };

    const containsItem = (node: React.ReactNode): boolean =>
      React.isValidElement(node) &&
      (node.type === SearchSelectItem ||
        React.Children.toArray((node.props as any)?.children).some(
          containsItem,
        ));

    // Filter items based on search term. Items nested in group wrappers
    // (e.g. a <div> with a heading) are filtered too, and a wrapper is
    // dropped once none of its items match.
    const filterNodes = (nodes: React.ReactNode): React.ReactNode[] =>
      React.Children.toArray(nodes).flatMap((child): React.ReactNode[] => {
        if (!React.isValidElement(child)) return [child];
        if (child.type === SearchSelectItem) {
          const childText = [
            extractTextContent((child.props as any).children),
            extractTextContent((child.props as any).description),
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return childText.includes(searchTerm.toLowerCase()) ? [child] : [];
        }
        if (!searchTerm || !containsItem(child)) return [child];
        const nested = filterNodes((child.props as any).children);
        if (!nested.some(containsItem)) return [];
        return [React.cloneElement(child, undefined, nested)];
      });

    const filteredChildren = filterNodes(children);

    const hasResults = filteredChildren.length > 0;

    return (
      <PopoverPrimitives.Portal container={portalContainer}>
        <PopoverPrimitives.Content
          ref={forwardedRef}
          className={cx(
            "relative z-50 overflow-hidden rounded-xl border border-border shadow-xl shadow-black/[2.5%]",
            "min-w-[calc(var(--radix-popover-trigger-width))] max-w-[95vw]",
            "max-h-[300px]",
            "bg-background/90 backdrop-blur-sm",
            "text-body",
            "will-change-[transform,opacity]",
            "data-[state=closed]:animate-hide",
            "data-[side=bottom]:animate-slideDownAndFade data-[side=left]:animate-slideLeftAndFade data-[side=right]:animate-slideRightAndFade data-[side=top]:animate-slideUpAndFade",
            className,
          )}
          side="bottom"
          sideOffset={8}
          align="start"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            const target = e.currentTarget as HTMLElement;
            const searchInput = target?.querySelector("input");
            searchInput?.focus();
            if (value) {
              const selectedItem = target?.querySelector(
                `[data-value="${CSS.escape(value)}"]`,
              ) as HTMLElement | null;
              const scroller = scrollerRef.current;
              if (selectedItem && scroller) {
                scroller.scrollTop =
                  selectedItem.offsetTop -
                  scroller.clientHeight / 2 +
                  selectedItem.clientHeight / 2;
              }
            }
          }}
          {...props}
          // Keep trackpad wheel gestures inside the popover so Radix Dialog's
          // document-level scroll lock does not intercept them.
          onWheel={(event) => event.stopPropagation()}
        >
          <div className="sticky top-0 z-10 bg-background p-1 shadow-sm">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="w-full border-none bg-transparent px-3 py-1.5 pl-8 text-body text-foreground outline-none placeholder:text-foreground-muted focus:ring-0"
                placeholder={searchPlaceholder}
                disabled={isDisabled}
              />
              <RiSearchLine
                className="absolute left-2 top-1/2 size-4 -translate-y-1/2 text-foreground-muted"
                aria-hidden="true"
              />
            </div>
          </div>
          <div
            ref={setScrollerNode}
            className="relative max-h-[200px] overflow-auto overscroll-contain p-1"
          >
            {!hasResults ? (
              <div className="px-3 py-2 text-body text-muted-foreground">
                {emptyMessage}
              </div>
            ) : (
              filteredChildren
            )}
          </div>
          {footerAction && (
            <div className="sticky bottom-0 border-t border-border bg-background p-1">
              {footerAction}
            </div>
          )}
        </PopoverPrimitives.Content>
      </PopoverPrimitives.Portal>
    );
  },
);

SearchSelectContent.displayName = "SearchSelectContent";

// Item component
interface SearchSelectItemProps {
  value: string;
  children: React.ReactNode;
  showCheckmark?: boolean;
  disabled?: boolean;
  description?: React.ReactNode;
  className?: string;
  /** Allow the label to wrap onto multiple lines instead of truncating */
  wrap?: boolean;
}

const SearchSelectItem = React.forwardRef<
  HTMLDivElement,
  SearchSelectItemProps
>(
  (
    {
      value: itemValue,
      children,
      showCheckmark = true,
      disabled,
      description,
      className,
      wrap,
      ...props
    },
    forwardedRef,
  ) => {
    const { value, onValueChange, disabled: rootDisabled } = useSearchSelect();
    const isDisabled = disabled || rootDisabled;

    const handleSelect = () => {
      if (isDisabled) return;
      const newValue = itemValue === value ? "" : itemValue;
      onValueChange(newValue);
    };

    return (
      <div
        ref={forwardedRef}
        data-value={itemValue}
        className={cx(
          "grid cursor-pointer gap-x-2 rounded-xl px-3 py-2 outline-none transition-colors sm:text-body",
          "text-foreground",
          "hover:bg-hover",
          "focus-visible:bg-hover",
          isDisabled && "pointer-events-none cursor-not-allowed opacity-50",
          value === itemValue && "font-medium text-primary bg-primary/10",
          className,
          showCheckmark && "grid-cols-[1fr_20px] gap-x-2",
        )}
        onClick={handleSelect}
        {...props}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-0.5 overflow-hidden">
          <span className={cx(wrap ? "min-w-0 break-words" : "truncate")}>
            {children}
          </span>
          {description && (
            <span className="min-w-0 break-words text-caption text-foreground-muted">
              {description}
            </span>
          )}
        </div>
        {showCheckmark && value === itemValue && (
          <RiCheckLine
            className="size-5 shrink-0 text-foreground"
            aria-hidden="true"
          />
        )}
      </div>
    );
  },
);

SearchSelectItem.displayName = "SearchSelectItem";

// Dynamic Select Item component
const DynamicSelectItem = React.forwardRef<
  HTMLDivElement,
  {
    onAddOption: (value: string) => void;
    onClose?: () => void;
    label?: string;
    addPlaceholder?: string;
    className?: string;
    initialValue?: string;
    /** Called when the inline input value changes */
    onInputChange?: (value: string) => void;
  }
>(
  (
    {
      onAddOption,
      onClose,
      label = "option",
      addPlaceholder = "Enter new option...",
      className,
      initialValue = "",
      onInputChange,
    },
    ref,
  ) => {
    const [isAdding, setIsAdding] = React.useState(false);
    const [newValue, setNewValue] = React.useState("");
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Try to get search context if available
    let searchTerm = "";
    let setOpen: ((open: boolean) => void) | null = null;
    try {
      const context = useSearchSelect();
      searchTerm = context.searchTerm;
      setOpen = context.setOpen;
    } catch {
      // Not in SearchSelect context, use initialValue or empty string
      searchTerm = initialValue;
    }

    React.useEffect(() => {
      if (isAdding && inputRef.current) {
        // Set the initial value when starting to add - use search term if available
        const val = searchTerm || initialValue;
        setNewValue(val);
        onInputChange?.(val);

        // Use setTimeout to ensure DOM is updated
        setTimeout(() => {
          inputRef.current?.focus();

          // Find the Radix Select viewport (the actual scrollable container)
          const viewport = inputRef.current?.closest(
            "[data-radix-select-viewport]",
          );
          if (viewport) {
            // Scroll to bottom of the viewport
            viewport.scrollTop = viewport.scrollHeight;
          } else {
            // Fallback: try to find any scrollable parent
            const scrollableParent =
              inputRef.current?.closest('[role="listbox"]') ||
              inputRef.current?.closest(
                ".overflow-hidden, .overflow-auto, .overflow-y-auto",
              );
            if (scrollableParent) {
              scrollableParent.scrollTop = scrollableParent.scrollHeight;
            }
          }
        }, 100);
      }
    }, [isAdding, initialValue, searchTerm]);

    const handleAdd = () => {
      if (newValue.trim()) {
        onAddOption(newValue.trim());
        setNewValue("");
        setIsAdding(false);
        // Close the search select automatically if we have access to the context
        if (setOpen) {
          setTimeout(() => {
            setOpen(false);
          }, 0);
        } else {
          setTimeout(() => {
            onClose?.();
          }, 0);
        }
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      e.stopPropagation(); // Prevent the select from handling these events
      if (e.key === "Enter") {
        e.preventDefault();
        handleAdd();
      } else if (e.key === "Escape") {
        setIsAdding(false);
        setNewValue("");
      }
    };

    if (isAdding) {
      return (
        <div ref={ref} className={cx("px-2 py-1.5", className)}>
          <div className="flex w-full items-center gap-2">
            <Input
              ref={inputRef}
              value={newValue}
              onChange={(e) => {
                e.stopPropagation(); // Prevent the select from handling input changes
                setNewValue(e.target.value);
                onInputChange?.(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              placeholder={addPlaceholder}
              className="h-8 flex-1"
            />
            <Button
              type="button"
              aria-label={label}
              onClick={handleAdd}
              disabled={!newValue.trim()}
              variant="secondary"
              className="h-8 w-8 p-0"
              icon={RiAddLine}
            />
          </div>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={cx(
          "relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-body outline-none hover:bg-accent hover:text-accent-foreground",
          className,
        )}
        onClick={() => setIsAdding(true)}
      >
        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
          <RiAddLine className="h-4 w-4" />
        </span>
        <Label>{label}</Label>
      </div>
    );
  },
);

DynamicSelectItem.displayName = "DynamicSelectItem";

export {
  DynamicSelectItem,
  SearchSelect,
  SearchSelectContent,
  SearchSelectItem,
  SearchSelectTrigger,
  SearchSelectValue,
  useSearchSelect,
};
